<template>
  <div class="flex flex-col h-full gap-8 overflow-y-hidden">
    <div class="flex flex-col gap-3 px-4 overflow-y-auto h-full">
      <!-- Provider Selection -->
      <div v-if="shouldShowClientsChips" class="flex flex-col gap-2">
        <span class="font-medium">נותן השירות</span>
        <select-provider
          v-model="selectedProvider"
          @provider-selected="onProviderSelected"
        />
      </div>

      <!-- Event Title -->
      <div v-if="!fullDay" class="w-full flex flex-col">
        <span class="mb-1 font-medium">שם האירוע</span>
        <input-text v-model="newEvent.title" placeholder="שם האירוע" type="text" />
      </div>

      <!-- Date Selection -->
      <div class="flex flex-col w-full gap-1">
        <span class="font-medium">באיזה תאריך?</span>
        <ResponsiveDatePicker
          v-model="newEvent.date"
          :minDate="today"
          title="בחירת תאריך" />
      </div>

      <!-- Time Selection -->
      <div v-if="!fullDay" class="w-full flex gap-3">
        <div class="flex flex-col gap-1 w-full">
          <span class="font-medium">שעת התחלה</span>
          <MobileTimePicker
            :visible="isTimePickerVisible"
            v-model="newEvent.from"
            is24Hour
            placeholder="בחרו שעה"
          />
        </div>

        <div class="flex flex-col gap-1 w-full">
          <span class="font-medium">שעת סיום</span>
          <MobileTimePicker
            :visible="isTimePickerVisible"
            v-model="newEvent.to"
            is24Hour
            placeholder="בחרו שעה"
          />
        </div>
      </div>

      <small v-if="invalidRange" class="text-red-500">שעת הסיום חייבת להיות אחרי שעת ההתחלה</small>


      <!-- Color Picker (manual events only, not full-day OOO) -->
      <div v-if="!fullDay" class="flex flex-col">
        <span class="mb-1 font-medium">צבע האירוע</span>
        <span class="text-xs text-gray-500 mb-2">הצבע יופיע ביומן. השאירו ריק לצבע ברירת המחדל.</span>
        <div class="flex items-center gap-2 flex-nowrap overflow-x-auto p-2">
          <button
            v-for="color of colorPalette"
            :key="color"
            type="button"
            :title="color"
            :class="['w-8 h-8 flex-none rounded-full border-2 transition-all',
                     newEvent.color === color ? 'border-gray-800 scale-110' : 'border-gray-200']"
            :style="{ backgroundColor: color }"
            @click="newEvent.color = color"
          />
          <button
            type="button"
            :class="['w-8 h-8 flex-none rounded-full border-2 flex items-center justify-center text-xs text-gray-500',
                     !newEvent.color ? 'border-gray-800 bg-gray-100' : 'border-gray-200 bg-white']"
            title="ברירת מחדל"
            @click="newEvent.color = undefined"
          >
            <i class="pi pi-times" style="font-size: 0.7rem" />
          </button>
        </div>
      </div>

      <!-- Full Day Toggle -->
      <div class="border border-gray-200 rounded-lg mt-1">
        <settings-toggle
          class="mt-1 mb-1"
          :status="fullDay"
          title="יום מלא"
          bg-color="white"
          @value-changed="(val) => {fullDay = val}" />
      </div>


      <div class="border border-gray-200 rounded-lg mt-1">
        <settings-toggle
          class="mb-1"
          :status="isRecurring"
          title="אירוע חוזר"
          bg-color="white"
          @value-changed="(val) => {isRecurring = val}" />

        <!-- Recurring Options (shown when recurring is enabled) -->
        <div v-if="isRecurring" class="flex flex-col gap-4 mx-4">
          <!-- Frequency Selection -->
          <div class="flex flex-col gap-1">
            <span class="font-medium">תדירות</span>
            <Select
              v-model="recurringOptions.frequency"
              :options="frequencyOptions"
              placeholder="בחירת תדירות"
              optionLabel="label"
              optionValue="value" />
          </div>

          <div v-if="isRecurring" class="flex flex-col gap-2">
            <span class="font-medium">סיום</span>
            <div class="flex gap-2 items-center justify-start">
              <RadioButton
                v-model="recurringOptions.endType"
                name="endCondition"
                value="never" />
              <span>ללא סיום</span>
            </div>
            <div class="flex gap-2 items-center justify-start">
              <RadioButton
                v-model="recurringOptions.endType"
                name="endCondition"
                value="date" />
              <span>עד תאריך</span>
              <ResponsiveDatePicker
                v-model="recurringOptions.endDate"
                placeholder="בחרו תאריך סיום"
                title="תאריך סיום"
                :minDate="date ? new Date(date.getTime() + 86400000) : new Date()"
              />
            </div>
          </div>

          <!-- Preview -->
          <div v-if="recurringPreview" class="text-xs text-gray-600 bg-white p-2 rounded border border-gray-200 mb-4">
            <i class="pi pi-info-circle ml-1" />
            {{ recurringPreview }}
          </div>

        </div>

        <!-- Conflicts Display -->
        <div v-if="conflicts && conflicts.length > 0"
             class="flex flex-col gap-2 p-3 bg-yellow-50 rounded-b-lg border border-yellow-300">
          <div class="flex items-center gap-2">
            <i class="pi pi-exclamation-triangle text-yellow-600" />
            <span class="font-medium text-sm">נמצאו {{ conflicts.length }} התנגשויות</span>
          </div>
          <div class="max-h-32 overflow-y-auto text-xs space-y-1">
            <div v-for="conflict in conflicts.slice(0, 5)" :key="conflict.date" class="text-gray-700">
              • {{ formatConflictDate(conflict.date) }}: {{ conflict.conflictingAppointmentTitle }}
            </div>
            <div v-if="conflicts.length > 5" class="text-gray-500 italic">
              ועוד {{ conflicts.length - 5 }} התנגשויות...
            </div>
          </div>
          <span class="text-xs text-gray-600">האם להמשיך בכל זאת?</span>
        </div>
      </div>
    </div>

    <div class="bg-white shadow-[0_-8px_16px_-4px_rgba(0,0,0,0.05)] mt-auto">
      <drawer-footer
        class=" px-4"
        :approveDisable="!validateFilled"
        :approveLoading="loading"
        @approve-click="clickedSave"
        @cancel-click="closeDrawer"
      />
    </div>

    <CancelAppointmentConfirm
      v-model:visible="showBulkCancelSmsChoice"
      title="ביטול תורים"
      message="נראה שיש תורים ביום זה, האם לבטל אותם?"
      :default-send-sms="adminAutomations.isCancellationAutomationEnabled"
      :automation-enabled="adminAutomations.isCancellationAutomationEnabled"
      @confirm="handleBulkCancelConfirm"
      @update:visible="onBulkCancelDialogVisible" />
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, reactive, ref, watch } from 'vue';
import { DateTime } from 'luxon';
import { useAdminAvailabilityStore, useAdminClientsStore, useAdminHomeStore, useAdminStore } from '@app/stores';
import {
  AppointmentType,
  IClient,
  IManualAppointment,
  IRecurringEventConflict,
  RecurringEventFrequency
} from '@torim-app/types';

import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'vue-toastification';
import { padNumber } from '@torim-app/utils';
import { AppointmentsApi } from '@app/api/admin';
import { RecurringEventsApi } from '@app/api/admin/recurring-events-api';
import Select from 'primevue/select';
import { formatConflictDate, getDayName } from '@app/utils/utils';
import { useDrawer } from '@torim-app/shared-components';
import SelectProvider from '@app/components/admin/components/SelectProvider.vue';
import ResponsiveDatePicker from '@app/components/admin/components/ResponsiveDatePicker.vue';
import { trackEvent } from '@app/composables/posthog';
import { useAdminAutomations } from '@app/stores/admin-automations';
import CancelAppointmentConfirm from '@app/components/admin/modals/CancelAppointmentConfirm.vue';

const props = defineProps({
  date: {
    type: Object as PropType<Date>,
    required: false,
    default: undefined
  }
});

const toast = useToast();
const adminAvailabilityStore = useAdminAvailabilityStore();
const adminStore = useAdminStore();
const adminHomeStore = useAdminHomeStore();
const adminAutomations = useAdminAutomations();
const { closeDrawer } = useDrawer();

const showBulkCancelSmsChoice = ref(false);
const pendingBulkCancel = ref<{ date: string; providerId: string } | null>(null);

const loading = ref<boolean>(false);
const isTimePickerVisible = ref<boolean>(false);
const fullDay = ref<boolean>(false);
const isRecurring = ref<boolean>(false);
const conflicts = ref<IRecurringEventConflict[]>([]);
const adminNote = ref<string>();
const selectedProvider = ref<string>(adminHomeStore.getSelectedProviderId || adminStore.userId);

const isMainClient = computed(() => adminStore.getMainClient);
const canManageOthers = computed(() => adminStore.getCanManageOthers);
const providers = computed<Array<Pick<IClient, 'userId' | 'fullName' | 'profileImage'>>>(() =>
  canManageOthers.value ? adminStore.getProviders : []
);
const shouldShowClientsChips = computed(() => canManageOthers.value && providers.value.length > 1);

const newEvent = reactive<{
  from: Date | null;
  to: Date | null;
  date: Date;
  title: string;
  color?: string;
}>({
  from: props.date ? props.date : null,
  to: props.date ? DateTime.fromJSDate(props.date).plus({ hour: 1 }).toJSDate() : null,
  date: props.date ? props.date : adminHomeStore.getSelectedDateAtCalendar,
  title: '',
  color: undefined
});

const colorPalette = ['#EF4444', '#F97316', '#EAB308', '#22C55E', '#14B8A6', '#3B82F6', '#8864EE', '#EC4899'];

const recurringOptions = reactive({
  frequency: RecurringEventFrequency.WEEKLY,
  endType: 'never' as 'never' | 'date',
  endDate: null as Date | null
});

const today = new Date();

const frequencyOptions = computed(() => {
  const dayName = getDayName(newEvent.date);
  return [
    { value: RecurringEventFrequency.DAILY, label: 'כל יום' },
    { value: RecurringEventFrequency.WEEKLY, label: `כל שבוע (${dayName})` },
    { value: RecurringEventFrequency.BI_WEEKLY, label: `כל שבועיים (${dayName})` },
    { value: RecurringEventFrequency.MONTHLY, label: `פעם בחודש (${dayName})` }

  ];
});

const recurringPreview = computed(() => {
  if (!isRecurring.value || !newEvent.date) return '';

  const bookingLimit = adminStore?.businessSiteData?.configurations?.allowToSchedule || 60;
  const generationLimit = DateTime.now().plus({ days: bookingLimit + 90 });

  let preview = 'יווצרו אירועים עד ';

  if (recurringOptions.endType === 'never') {
    preview += generationLimit.toFormat('dd/MM/yyyy');
    preview += ' (מגבלת הזמנה + 90 יום)';
  } else if (recurringOptions.endDate) {
    const endDate = DateTime.fromJSDate(recurringOptions.endDate);
    preview += endDate.toFormat('dd/MM/yyyy');
  }

  return preview;
});

const validateFilled = computed(() => {
  if (!fullDay.value)
    return !!newEvent.date && !!newEvent.from && !!newEvent.to && !!newEvent.title && isValid.value;
  else
    return !!newEvent.date;
});

const toDecimal = (d: Date) => {
  const dt = DateTime.fromJSDate(d);
  return dt.hour + dt.minute / 60;
};

const invalidRange = computed(() => !!newEvent.from && !!newEvent.to && toDecimal(newEvent.to) <= toDecimal(newEvent.from));
const isValid = computed(() => !!newEvent.from && !!newEvent.to && !invalidRange.value);

const onProviderSelected = (provider: string) => {
  selectedProvider.value = provider;
};

const checkForConflicts = async () => {
  if (!isRecurring.value) {
    conflicts.value = [];
    return;
  }

  try {
    // Mirror createRecurringEvent: full-day blocks the entire 0-24 window.
    const timeFrom = fullDay.value
      ? 0
      : DateTime.fromJSDate(newEvent.from!).hour + (DateTime.fromJSDate(newEvent.from!).minute / 60);
    const timeTo = fullDay.value
      ? 24
      : DateTime.fromJSDate(newEvent.to!).hour + (DateTime.fromJSDate(newEvent.to!).minute / 60);

    const response = await RecurringEventsApi.checkConflicts({
      title: newEvent.title,
      timeFrom,
      timeTo,
      providingClient: selectedProvider.value,
      type: fullDay.value ? AppointmentType.OUT_OF_OFFICE : AppointmentType.MANUAL,
      frequency: recurringOptions.frequency,
      startDate: DateTime.fromJSDate(newEvent.date).startOf('day').toISO()!,
      endDate: recurringOptions.endType === 'date' && recurringOptions.endDate
        ? DateTime.fromJSDate(recurringOptions.endDate).startOf('day').toISO()!
        : undefined
    });

    conflicts.value = response.conflicts;
  } catch (e) {
    console.error('Failed to check conflicts:', e);
    toast.error('לא הצלחנו לבדוק התנגשויות');
  }
};

const clickedSave = async () => {
  trackEvent('manual_event_save_clicked');
  loading.value = true;

  if (isRecurring.value) {
    await createRecurringEvent();
  } else {
    if (fullDay.value)
      await clickOutOfOffice();
    else
      await newManualEvent();
  }

  loading.value = false;
  // When OOO finds existing appointments, the bulk-cancel confirm is shown and it
  // owns drawer closure (otherwise the drawer would unmount the dialog).
  if (!showBulkCancelSmsChoice.value) {
    closeDrawer();
  }
};

const createRecurringEvent = async () => {
  trackEvent('recurring_event_created');
  try {
    // For full-day recurring OOO events, block the entire day (0-24) rather than
    // using today's working hours. Working hours can differ across occurrences,
    // and may be undefined for days with no availability — which would cause the
    // backend to reject the event for missing timeFrom/timeTo.
    const selectedTimeFrom = fullDay.value
      ? 0
      : DateTime.fromJSDate(newEvent.from!).hour + (DateTime.fromJSDate(newEvent.from!).minute / 60);

    const selectedTimeTo = fullDay.value
      ? 24
      : DateTime.fromJSDate(newEvent.to!).hour + (DateTime.fromJSDate(newEvent.to!).minute / 60);

    await RecurringEventsApi.createRecurringEvent({
      title: fullDay.value ? 'יום ללא עבודה' : newEvent.title,
      timeFrom: selectedTimeFrom,
      timeTo: selectedTimeTo,
      providingClient: selectedProvider.value,
      type: fullDay.value ? AppointmentType.OUT_OF_OFFICE : AppointmentType.MANUAL,
      frequency: recurringOptions.frequency,
      startDate: DateTime.fromJSDate(newEvent.date).startOf('day').toISO()!,
      endDate: recurringOptions.endType === 'date' && recurringOptions.endDate
        ? DateTime.fromJSDate(recurringOptions.endDate).startOf('day').toISO()!
        : undefined,
      adminNote: adminNote.value,
      color: fullDay.value ? undefined : newEvent.color
    });

    toast.success('אירוע חוזר נוצר בהצלחה!');
    await adminHomeStore.fetchBusinessCalendarDataSilently();
    closeDrawer();
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו ליצור אירוע חוזר, אנא נסו שוב');
  }
};

const clickOutOfOffice = async () => {
  trackEvent('out_of_office_created');
  const { start, end } = adminAvailabilityStore.getWorkingHoursRangeToday(selectedProvider.value);
  const date = DateTime.fromJSDate(newEvent.date).startOf('day');
  const event: IManualAppointment = {
    date: date.toJSDate(),
    timeTo: end,
    timeFrom: start,
    title: 'יום ללא עבודה',
    type: AppointmentType.OUT_OF_OFFICE,
    providerId: selectedProvider.value
  };

  await adminHomeStore.addManualEventToCalendar(event);
  const dateFormatted = `${date.year}-${padNumber(date.month)}-${padNumber(date.day)}`;
  const hasAppointments = adminHomeStore.getCalendarDataByDate(dateFormatted).filter(x => x.type === AppointmentType.EVENT).length > 0;

  if (hasAppointments) {
    // Keep the drawer mounted so the dialog (rendered in this template) stays in the tree.
    pendingBulkCancel.value = { date: dateFormatted, providerId: selectedProvider.value };
    showBulkCancelSmsChoice.value = true;
  }
  // No appointments → fall through; clickedSave closes the drawer.
};

const handleBulkCancelConfirm = async (sendSms: boolean, reason?: string) => {
  const pending = pendingBulkCancel.value;
  pendingBulkCancel.value = null;
  if (pending) {
    await executeBulkCancel(pending.date, pending.providerId, !sendSms, reason);
  }
  closeDrawer();
};

// Closes the drawer if the user dismisses the dialog (X / backdrop) without confirming.
const onBulkCancelDialogVisible = (visible: boolean) => {
  if (!visible && pendingBulkCancel.value) {
    pendingBulkCancel.value = null;
    closeDrawer();
  }
};

const executeBulkCancel = async (date: string, providerId: string, skipNotification: boolean, reason?: string) => {
  try {
    await AppointmentsApi.cancelAppointment({ date, providerId, skipNotification, reason });
    await adminHomeStore.fetchBusinessCalendarDataSilently();
    toast.success('כל התורים בוטלו בהצלחה');
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לבטל את התורים, אנא נסו שוב');
  }
};

const newManualEvent = async () => {
  trackEvent('manual_event_created');
  console.log(selectedProvider.value);
  try {
    const selectedTimeFrom = DateTime.fromJSDate(newEvent.from!);
    const selectedTimeTo = DateTime.fromJSDate(newEvent.to!);
    const params: IManualAppointment = {
      date: DateTime.fromJSDate(newEvent.date).startOf('day').toJSDate(),
      timeTo: selectedTimeTo.hour + (selectedTimeTo.minute / 60),
      timeFrom: selectedTimeFrom.hour + (selectedTimeFrom.minute / 60),
      title: newEvent.title,
      type: AppointmentType.MANUAL,
      adminNote: adminNote.value,
      providerId: selectedProvider.value,
      color: newEvent.color,
    };
    await adminHomeStore.addManualEventToCalendar(params);
    closeDrawer();
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו להוסיף אירוע, אנא נסו שוב');
  }
};

watch(() => newEvent?.from, () => {
  if (newEvent.from && !newEvent.to)
    newEvent.to = DateTime.fromJSDate(newEvent.from).plus({ hour: 1 }).toJSDate();
});

// Check for conflicts when recurring options change
watch([
  () => isRecurring.value,
  () => fullDay.value,
  () => recurringOptions.frequency,
  () => recurringOptions.endType,
  () => recurringOptions.endDate,
  () => newEvent.date,
  () => newEvent.from,
  () => newEvent.to
], () => {
  if (isRecurring.value && (fullDay.value || (newEvent.from && newEvent.to))) {
    checkForConflicts();
  } else {
    conflicts.value = [];
  }
});
</script>
